import { useEffect, useRef, useState } from "react";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { FacilityMap } from "@/components/Map/FacilityMap";
import { NearbyFacilityItem } from "@/components/facility/NearbyFacilityItem";
import type { UnitGroup } from "@/types";

interface NearbyPanelProps {
    unitGroup: UnitGroup;
}

/**
 * Presentational nearby-units panel: title, facility cards (2-up via container
 * query), and the map. Shared by the row expansion (NearbyCollapse) and the
 * grid expansion (GridNearbyExpansion) so both render identically.
 */
export const NearbyPanel = ({ unitGroup }: NearbyPanelProps) => {
    const { facilityMap } = useDisplayOptionContext();
    const facilities = unitGroup.nearby_units.map((unit) => unit.facility);
    const facilityUrls = Object.fromEntries(
        facilities.map((f) => [f.id, facilityMap[f.id]?.url ?? ""]),
    );
    const nearbyUnitTypeName =
        unitGroup.representative_unit?.unit_type_name ?? "";

    // Probe the theme's primary color so the map pins match the brand.
    const primaryProbeRef = useRef<HTMLSpanElement>(null);
    const [pinColor, setPinColor] = useState<string | undefined>(undefined);
    useEffect(() => {
        if (!primaryProbeRef.current) return;
        const color = getComputedStyle(primaryProbeRef.current).backgroundColor;
        if (color && color !== "rgba(0, 0, 0, 0)" && color !== "transparent") {
            setPinColor(color);
        }
    }, []);

    return (
        <>
            <span
                ref={primaryProbeRef}
                className="tw:bg-primary"
                aria-hidden="true"
                style={{ display: "none" }}
            />
            <div className="unit-item__nearby-panel tw:@container tw:w-full tw:p-6 tw:bg-white tw:rounded-lg tw:shadow">
                <h4 className="unit-item__nearby-title tw:text-md tw:mb-4">
                    Similar sized units are available at these nearby locations
                </h4>

                {unitGroup.nearby_units.length === 0 ? (
                    <p className="unit-item__nearby-empty tw:text-sm tw:text-gray-600">
                        No nearby units available.
                    </p>
                ) : (
                    <div className="unit-item__nearby-list tw:grid tw:grid-cols-1 tw:gap-6">
                        <div className="unit-item__nearby-cards tw:grid tw:grid-cols-1 tw:@2xl:grid-cols-2 tw:gap-4 tw:content-start">
                            {unitGroup.nearby_units.map((unit, index) => (
                                <NearbyFacilityItem
                                    key={unit.facility.id}
                                    distance={unit.facility.distance}
                                    address1={unit.facility.address_1}
                                    address2={unit.facility.address_2}
                                    city={unit.facility.city}
                                    state={unit.facility.state}
                                    zip={unit.facility.zipcode}
                                    name={unit.facility.name}
                                    phone={unit.facility.phone}
                                    imageUrl={
                                        facilityMap[unit.facility.id]
                                            ?.featuredImage || ""
                                    }
                                    facilityUrl={
                                        facilityMap[unit.facility.id]?.url || ""
                                    }
                                    reviewsScore={
                                        facilityMap[unit.facility.id]
                                            ?.reviewsScore
                                    }
                                    reviewsCount={
                                        facilityMap[unit.facility.id]
                                            ?.reviewsCount
                                    }
                                    unitTypeName={
                                        unitGroup?.representative_unit
                                            ?.unit_type_name
                                    }
                                    pinNumber={index + 1}
                                />
                            ))}
                        </div>

                        <div className="unit-item__nearby-map tw:relative tw:w-full tw:h-[300px] tw:@2xl:h-[400px] tw:rounded-lg tw:overflow-hidden">
                            <FacilityMap
                                facilities={facilities}
                                height={"100%"}
                                width={"100%"}
                                facilityUrls={facilityUrls}
                                unitTypeName={nearbyUnitTypeName}
                                {...(pinColor ? { pinColor } : {})}
                            ></FacilityMap>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
